import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ArrowLeft, Database, Loader2 } from 'lucide-react'
import { RootState } from '../store'
import { isAdminUser } from '../utils/admin'
import adminDbService, { type AdminDbTableInfo } from '../services/adminDbService'

function tableGroup(table: string): string {
  if (table.startsWith('academ_')) return 'Academ'
  if (table.startsWith('chess_') || table.startsWith('stockfish_')) return 'Chess'
  if (table.startsWith('formula1_') || table.startsWith('public_formula1_')) return 'Formula 1'
  if (table.startsWith('golf_')) return 'Golf'
  if (table.startsWith('how_i_eat_')) return 'How I Eat'
  if (
    table.startsWith('restaurant_') ||
    table.startsWith('menu_') ||
    table.startsWith('dish') ||
    table.startsWith('cuisine') ||
    table.startsWith('ingredient') ||
    table === 'country_cities'
  ) {
    return 'Restaurant & Menu'
  }
  if (
    table.startsWith('agent_') ||
    table.startsWith('ai_') ||
    table === 'payments' ||
    table.startsWith('social_')
  ) {
    return 'Platform & AI'
  }
  return 'Other'
}

const AdminDbTables: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)
  const [tables, setTables] = useState<AdminDbTableInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!isAdminUser(user)) navigate('/dashboard', { replace: true })
  }, [user, navigate])

  useEffect(() => {
    if (!isAdminUser(user)) return
    setLoading(true)
    adminDbService
      .listTables()
      .then(setTables)
      .catch(() => setTables([]))
      .finally(() => setLoading(false))
  }, [user])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return tables
    return tables.filter(
      (t) => t.table.toLowerCase().includes(q) || t.entity.toLowerCase().includes(q),
    )
  }, [tables, search])

  const grouped = useMemo(() => {
    const map = new Map<string, AdminDbTableInfo[]>()
    for (const t of filtered) {
      const g = tableGroup(t.table)
      if (!map.has(g)) map.set(g, [])
      map.get(g)!.push(t)
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [filtered])

  if (!isAdminUser(user)) return null

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16 sm:pt-20 lg:pt-24 pb-8 sm:pb-12">
        <div className="container mx-auto px-3 xs:px-4 sm:px-6 max-w-5xl">
          <div className="mb-6 flex items-center gap-3">
            <Link to="/admin" className="p-2 rounded-xl hover:bg-muted/40 text-muted-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-display text-2xl font-bold text-foreground">Database tables</h1>
              <p className="text-sm text-muted-foreground">
                Non–BreakRoomCup tables · add, edit, delete rows
              </p>
            </div>
          </div>

          <input
            className="mb-6 w-full px-3 py-2 rounded-xl border border-border bg-background text-foreground text-sm"
            placeholder="Search tables…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {loading ? (
            <div className="py-16 text-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
              Loading tables…
            </div>
          ) : grouped.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground rounded-2xl border border-border">
              No tables found
            </div>
          ) : (
            <div className="space-y-8">
              {grouped.map(([group, items]) => (
                <section key={group}>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                    {group}
                  </h2>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {items.map((t) => (
                      <Link
                        key={t.table}
                        to={`/admin/db/${encodeURIComponent(t.table)}`}
                        className="flex items-center gap-3 rounded-xl border border-border bg-card/50 px-4 py-3 hover:border-primary/40 hover:bg-card transition-all"
                      >
                        <Database className="w-4 h-4 text-primary shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-foreground truncate">{t.table}</p>
                          <p className="text-xs text-muted-foreground">
                            {t.columnCount} columns · PK: {t.primaryKey.join(', ')}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default AdminDbTables
